import { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  getSearchState,
  setSearchLimit,
} from "src/features/projectGroups/state/projectGroupsSlice";
import { Dropdown } from "../Input";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "src/common/state/hooks";
import {
  getMediaSearch,
  getNumberOfMediaSearchHits,
  setMediaSearch,
} from "src/features/gallery/state/gallerySlice";
import { PageChangeArgs } from "src/features/gallery/useGalleryPagination";

const pgValues = [5, 10, 20, 50];
const galleryValues = [20, 50, 100, 200];

const buildPageList = ({
  currentPage,
  totalPages,
  windowSize = 2,
}: {
  currentPage: number;
  totalPages: number;
  windowSize?: number;
}): (number | "…")[] => {
  const pages: (number | "…")[] = [];
  const left = Math.max(0, currentPage - windowSize);
  const right = Math.min(totalPages - 1, currentPage + windowSize);

  // Always include first page
  pages.push(0);
  if (left > 1) pages.push("…");

  // Middle range
  for (let i = left; i <= right; i++) {
    if (i !== 0 && i !== totalPages - 1) {
      pages.push(i);
    }
  }

  if (right < totalPages - 2) pages.push("…");

  // Always include last page
  if (totalPages > 1) pages.push(totalPages - 1);

  return pages;
};

export const PaginationControls = ({
  currentPage,
  totalPages,
  onPageChange,
  isLoading = false,
  galleryMode = false,
  className = "",
}: {
  currentPage: number;
  onPageChange: (args: PageChangeArgs) => void;
  isLoading: boolean;
  galleryMode?: boolean;
  className?: string;
  totalPages;
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  // Both selectors must run unconditionally (Rules of Hooks): pick the value
  // afterwards rather than calling useSelector inside a ternary.
  const mediaSearch = useSelector(getMediaSearch);
  const searchState = useSelector(getSearchState);
  const { limit } = galleryMode ? mediaSearch : searchState;
  const numberOfMediaHits = useSelector(getNumberOfMediaSearchHits);

  // 🔢 Dynamically calculate total pages
  const effectiveTotalPages = galleryMode ? Math.ceil(numberOfMediaHits / limit) : totalPages; // fallback is passed in via props (if galleryMode is false)

  const handleDropdownChange = (e: any) => {
    const value = parseInt(e.target.value, 10);
    if (galleryMode) {
      dispatch(setMediaSearch({ limit: value }));
    } else {
      dispatch(setSearchLimit(value));
    }
    onPageChange({ newLimit: value }); // Reset to page 0
  };

  const dropdownValues = galleryMode ? galleryValues : pgValues;
  const entriesOptions = dropdownValues.map((val) => ({
    value: val.toString(),
    optionText: val.toString(),
  }));

  const pageItems = useMemo(() => {
    return buildPageList({
      currentPage,
      totalPages: effectiveTotalPages,
    });
  }, [currentPage, effectiveTotalPages]);

  const atFirstPage = currentPage === 0;
  const atLastPage = currentPage >= effectiveTotalPages - 1;

  const handlePageDbChange = (value: string) => {
    handleDropdownChange({ target: { value } });
  };

  return (
    <div
      id="pagination-controls"
      className={`flex justify-between items-center my-4 px-2 ${className}`}
    >
      {/* Per-page selector */}
      <div className="flex items-center gap-2">
        <Dropdown
          options={entriesOptions}
          value={limit.toString()}
          onChangeCb={handlePageDbChange}
          width="w-20"
          borderOff
          mobile
          asDefaultValue={true}
        />
        <span className="text-sm text-gray-700">{t("general.perPage")}</span>
      </div>

      {/* Pagination buttons */}
      <div className="flex items-center gap-1 text-sm">
        <button
          onClick={() => onPageChange({ newPage: 0 })}
          disabled={atFirstPage || isLoading}
          className="px-2 py-1 rounded hover:bg-gray-200 disabled:text-gray-400"
        >
          {t("general.firstPage")}
        </button>
        <button
          onClick={() => onPageChange({ newPage: Math.max(currentPage - 1, 0) })}
          disabled={atFirstPage || isLoading}
          className="px-2 py-1 rounded hover:bg-gray-200 disabled:text-gray-400"
        >
          {"<"}
        </button>

        {pageItems.map((p, idx) =>
          p === "…" ? (
            <span key={`dots-${idx}`} className="px-2">
              …
            </span>
          ) : (
            <button
              key={p}
              onClick={() => onPageChange({ newPage: p as number })}
              disabled={isLoading}
              className={`px-2 py-1 rounded hover:bg-gray-200 ${
                p === currentPage ? "font-bold underline" : ""
              }`}
            >
              {p + 1}
            </button>
          ),
        )}

        <button
          onClick={() =>
            onPageChange({ newPage: Math.min(currentPage + 1, effectiveTotalPages - 1) })
          }
          disabled={atLastPage || isLoading}
          className="px-2 py-1 rounded hover:bg-gray-200 disabled:text-gray-400"
        >
          {">"}
        </button>
        <button
          onClick={() => onPageChange({ newPage: effectiveTotalPages - 1 })}
          disabled={atLastPage || isLoading}
          className="px-2 py-1 rounded hover:bg-gray-200 disabled:text-gray-400"
        >
          {t("general.lastPage")}
        </button>
      </div>
    </div>
  );
};
